// Top-centre HUD notices (ui-flow §6.6):
// - the "Laggy connection" chip while no FRAME has arrived for more than 250 ms;
// - sticky banners (serverRestart, roomClosing, and in money rooms idleWarning / idleFrozen)
//   until they no longer apply, counting down locally from the NOTICE's seconds argument. The
//   idle warning goes when its countdown ends (idleFrozen replaces it) or the player moves.
//
// Implementer: client UI. Consumer: app.ts.

import { h, show } from '../dom.js';
import { t } from '../i18n/t.js';

export type BannerKind = 'serverRestart' | 'roomClosing' | 'idleWarning' | 'idleFrozen';

export interface BannersHud {
  readonly el: HTMLElement;
  setLaggy(laggy: boolean): void;
  /** Starts (or restarts) a countdown banner from `seconds` at `nowMs`. */
  showBanner(kind: BannerKind, seconds: number, nowMs: number): void;
  clearBanner(kind?: BannerKind): void;
  /** Call every display frame; refreshes countdowns (DOM only on change). */
  tick(nowMs: number): void;
}

/** `mm:ss` for the roomClosing banner (protocol §5.6). */
export function formatClock(totalSec: number): string {
  const s = Number.isFinite(totalSec) && totalSec > 0 ? Math.ceil(totalSec) : 0;
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm < 10 ? '0' : ''}${mm}:${ss < 10 ? '0' : ''}${ss}`;
}

/** Banner text with `secondsLeft` remaining. */
export function bannerText(kind: BannerKind, secondsLeft: number): string {
  const left = Math.max(0, Math.ceil(secondsLeft));
  switch (kind) {
    case 'serverRestart':
      return t('notice.serverRestart', { s: left });
    case 'roomClosing':
      return t('notice.roomClosing', { 'mm:ss': formatClock(left) });
    case 'idleWarning':
      return t('notice.idleWarning', { s: left });
    case 'idleFrozen':
      return t('notice.idleFrozen', { s: left });
  }
}

/** Banners stay up this long after their countdown ends, while the server finishes. */
const BANNER_GRACE_MS = 15_000;

interface Banner {
  readonly el: HTMLElement;
  endsAtMs: number;
  /** The NOTICE's seconds: the countdown never shows more (a frame clock older than the NOTICE). */
  maxSec: number;
  text: string;
}

export function createBanners(): BannersHud {
  const laggy = h(
    'div',
    { class: 'hud-chip hud-laggy', role: 'status', hidden: true },
    h('span', { class: 'hud-laggy-icon', 'aria-hidden': 'true' }),
    t('hud.laggy'),
  );
  const el = h('div', { class: 'hud-top' }, laggy);
  const banners = new Map<BannerKind, Banner>();

  const remove = (kind: BannerKind): void => {
    const b = banners.get(kind);
    if (!b) return;
    banners.delete(kind);
    b.el.remove();
  };

  const tick = (nowMs: number): void => {
    for (const [kind, b] of banners) {
      const leftMs = b.endsAtMs - nowMs;
      if (leftMs < (kind === 'idleWarning' ? 0 : -BANNER_GRACE_MS)) {
        remove(kind);
        continue;
      }
      const text = bannerText(kind, Math.min(b.maxSec, leftMs / 1000));
      if (text !== b.text) {
        b.text = text;
        b.el.textContent = text;
      }
    }
  };

  return {
    el,
    setLaggy(on: boolean): void {
      if (laggy.hidden === !on) return;
      show(laggy, on);
    },
    showBanner(kind: BannerKind, seconds: number, nowMs: number): void {
      let b = banners.get(kind);
      if (!b) {
        b = {
          el: h('div', { class: 'hud-banner', role: 'status' }),
          endsAtMs: 0,
          maxSec: 0,
          text: '',
        };
        banners.set(kind, b);
        el.prepend(b.el);
      }
      b.endsAtMs = nowMs + Math.max(0, seconds) * 1000;
      b.maxSec = Math.max(0, seconds);
      b.text = '';
      tick(nowMs);
    },
    clearBanner(kind?: BannerKind): void {
      if (kind) remove(kind);
      else for (const k of [...banners.keys()]) remove(k);
    },
    tick,
  };
}
